import { QueueItem } from 'api/queueItems/queueItems.types'
import { getOptimizationFailureExplanationKey } from 'helpers/optimize.helper/optimize.helper'
import { getSubtypeDisplayName } from 'utils/systemUtils'
import { deriveQueueItemPhaseDurations } from '../OptJobPhaseProgress.helper'
import { OptimizationStep } from '../OptJobPhaseProgress.types'

export type QueueItemRow = {
  id: number
  /**
   * The subtype's display name, falling back to the raw subtype. Null when the
   * queue item has no subtype of its own — how that reads is the renderer's call,
   * since a lone unlabelled row and one among several want different treatment.
   */
  label: string | null
  steps: OptimizationStep[]
  /**
   * The item's whole life span: `created_at` until it ends, ticking with the
   * shared `now` while it is still going. Reused from the phase derivation so the
   * row total can never disagree with the phases it is made of.
   */
  elapsedMs: number
  /**
   * Translation key for why this item ended without a result, null when it did not. Carried on
   * the row so the matrix can explain a red cell without knowing what a queue item status is.
   */
  failureExplanationKey: string | null
}

/**
 * Projects each queue item to a matrix row: a stable id, a display label
 * (the subtype's display name, the raw subtype, or null), and the derived
 * 3-phase step list plus whole-item elapsed time from
 * `deriveQueueItemPhaseDurations`. The shared `now` is threaded through so
 * every row's active-phase timer stays in sync with the card's overall clock.
 * Ordering follows the incoming list; the caller is responsible for sorting.
 */
export function deriveQueueItemRows(
  items: QueueItem[],
  now: number,
  availableSubtypes: Subtype[] = [],
  jobHasEnded = false
): QueueItemRow[] {
  return items.map((item) => {
    const { steps, overallElapsedMs } = deriveQueueItemPhaseDurations(item, now, jobHasEnded)

    return {
      id: item.id,
      label: getSubtypeDisplayName(item.subtype, availableSubtypes),
      steps,
      elapsedMs: overallElapsedMs,
      failureExplanationKey: getOptimizationFailureExplanationKey(item.status) ?? null,
    }
  })
}

/**
 * Counts how many queue items have reached the `Finished` terminal state, out
 * of the total.
 */
export function countFinished(items: QueueItem[]): { finished: number; total: number } {
  let finished = 0
  for (const item of items) {
    if (item.status === 'Finished') finished += 1
  }
  return { finished, total: items.length }
}
